import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Chip, Collapse, List, ListItem, ListItemText, Typography } from '@mui/material'
import type { BulkOperationResult } from '@/api/bulk'

/**
 * Итог массовой операции: счётчики и раскрываемый список причин по элементам.
 * Причины приходят машинными кодами — переводим их здесь.
 */
export function BulkResultSummary({ result }: { result: BulkOperationResult }) {
  const { t } = useTranslation()
  const [detailsOpen, setDetailsOpen] = useState(false)

  const counters: Array<{ key: keyof BulkOperationResult; color: 'success' | 'info' | 'warning' | 'error' }> = [
    { key: 'created', color: 'success' },
    { key: 'updated', color: 'info' },
    { key: 'deleted', color: 'success' },
    { key: 'skipped', color: 'warning' },
    { key: 'failed', color: 'error' },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {counters.map(({ key, color }) => {
          const value = result[key]
          if (typeof value !== 'number' || value === 0) return null
          return (
            <Chip
              key={key}
              size="small"
              color={color}
              label={`${t(`bulk.counters.${key}`)}: ${value}`}
            />
          )
        })}
      </Box>

      {result.details.length > 0 && (
        <Box>
          <Button size="small" onClick={() => setDetailsOpen((prev) => !prev)}>
            {detailsOpen ? t('bulk.hideDetails') : t('bulk.showDetails', { count: result.details.length })}
          </Button>
          <Collapse in={detailsOpen}>
            <List dense>
              {result.details.map((detail, index) => (
                <ListItem key={`${detail.reason}-${index}`} disableGutters>
                  <ListItemText
                    primary={t(`bulk.reasons.${detail.reason ?? 'unknown'}`, {
                      defaultValue: detail.reason ?? '',
                    })}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {[
                          detail.word_id ? `word #${detail.word_id}` : null,
                          detail.card_id ? `card #${detail.card_id}` : null,
                          detail.student_id ? `student #${detail.student_id}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}
    </Box>
  )
}
